import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { Card } from "clutch/src/Card/Card.jsx";

import Caret from "@/inline-assets/blitz-caret-down.svg";
import { Container, List } from "@/shared/ListItemsTruncated.style.jsx";
import { classNames } from "@/util/class-names.mjs";

type ListItemsProps<T> = {
  title: string;
  headerControls: React.ReactNode;
  initialCount?: number;
  list: T[];
  itemLeftContent?: (item: T) => React.ReactNode;
  itemRightContent?: (item: T) => React.ReactNode;
  itemTooltip?: (item: T) => React.ReactNode;
  itemLinkFormat?: (item: T) => string;
  showMoreOverrideFn?: () => void;
  onShowMore?: () => void;
  className?: string;
  dontTruncate?: boolean;
};

function ListItemsTruncated<T>({
  dontTruncate = false,
  title,
  headerControls,
  initialCount = 4,
  list = [],
  itemLeftContent = () => null,
  itemRightContent = () => null,
  itemLinkFormat = () => null,
  itemTooltip = () => null,
  showMoreOverrideFn,
  onShowMore = () => {},
  className,
}: ListItemsProps<T>) {
  const { t } = useTranslation();
  const [showingMore, setShowingMore] = useState(false);
  const hasMore = list.length > initialCount;
  const renderedList = dontTruncate
    ? list
    : hasMore && !showingMore
      ? list.slice(0, initialCount)
      : list;

  return (
    <Card
      title={title}
      padding="0"
      headerControls={headerControls}
      classNameOuter={Container()}
      {...classNames(className, hasMore && showingMore && "showing-more")}
      data-showing-more={hasMore && showingMore}
      footerButtonText={
        dontTruncate
          ? null
          : hasMore && showingMore
            ? t("common:showLess", "Show less")
            : hasMore
              ? t("common:showAll", "Show All")
              : null
      }
      footerButtonIcon={<Caret />}
      footerButtonFn={() => {
        if (typeof showMoreOverrideFn === "function") showMoreOverrideFn();
        else if (hasMore) {
          setShowingMore((prev) => !prev);
          if (typeof onShowMore === "function") onShowMore();
        }
      }}
    >
      <List {...classNames(hasMore && "has-more")}>
        {renderedList.map((item, i) => (
          <ListItem
            key={i}
            leftContent={itemLeftContent(item)}
            rightContent={itemRightContent(item)}
            link={itemLinkFormat(item)}
            tooltip={itemTooltip(item)}
          />
        ))}
      </List>
    </Card>
  );
}

function ListItem({ leftContent, rightContent, link, tooltip }) {
  return (
    <li>
      {link ? (
        <a href={link} className="contents" data-tip={tooltip}>
          {leftContent && <div className="left">{leftContent}</div>}
          {rightContent && <div className="right">{rightContent}</div>}
        </a>
      ) : (
        <div className="contents" data-tip={tooltip}>
          {leftContent && <div className="left">{leftContent}</div>}
          {rightContent && <div className="right">{rightContent}</div>}
        </div>
      )}
    </li>
  );
}

ListItemsTruncated.ListItem = ListItem;

export default ListItemsTruncated;
